import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { useFormik } from "formik";
import { type FC } from "react";
import { useEffect } from "react";

import {
  toEmittedValue,
  toFormValues,
} from "~/components/UI/FilterRange/utils";
import type { IRangeValue } from "~/types/range";

import { filterRangeSchema, type IFilterRangeFormValues } from "../schema";
import * as Styled from "./FilterRangeForm.styles";
import { InputRange } from "./InputRange";

export interface FilterRangeFormProps {
  initialValue?: IRangeValue;
  onApply?: (value: IRangeValue) => void;
  getValues?: (value: IRangeValue) => void;
  isMobile?: boolean;
}

export const FilterRangeForm: FC<FilterRangeFormProps> = ({
  initialValue,
  onApply,
  getValues,
}) => {
  const formik = useFormik<IFilterRangeFormValues>({
    initialValues: toFormValues(initialValue),
    validationSchema: filterRangeSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (onApply) {
        onApply(toEmittedValue(values));
      }
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (getValues) {
        getValues(toEmittedValue(formik.values));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formik.values, getValues]);

  return (
    <Styled.FormContainer noValidate onSubmit={formik.handleSubmit}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1
        }}>
        <Tooltip
          title={formik.errors.min ?? ""}
          open={Boolean(formik.errors.min)}
          arrow
        >
          <span style={{ flexGrow: 1 }}>
            <InputRange
              name="min"
              value={formik.values.min}
              onChange={(min) => formik.setFieldValue("min", min)}
              error={Boolean(formik.errors.min)}
              placeholder="Min"
            />
          </span>
        </Tooltip>

        <Typography variant="caption" sx={{
          color: "text.secondary"
        }}>
          –
        </Typography>

        <Tooltip
          title={formik.errors.max ?? ""}
          open={Boolean(formik.errors.max)}
          arrow
        >
          <span style={{ flexGrow: 1 }}>
            <InputRange
              name="max"
              value={formik.values.max}
              onChange={(max) => formik.setFieldValue("max", max)}
              error={Boolean(formik.errors.max)}
              placeholder="Max"
            />
          </span>
        </Tooltip>
      </Stack>

      {onApply && (
        <Button variant="contained" size="small" type="submit">
          Apply
        </Button>
      )}
    </Styled.FormContainer>
  );
};
